import React from 'react';
import { useParams } from 'react-router-dom';
import { formatHUF, getAmount, useFinancialTransactions, useCampaigns, Skeleton } from '@rehome/shared';
import { TrendingUp, TrendingDown, PieChart as PieIcon, Wallet, ArrowUpRight, Gift, Repeat } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

// ─── FinancialDashboardPage ─────────────────────────────────────────────────
// ReHome-only financial transactions (Donation, Adoption, Subscription, CampaignPayout)
// Scoped to the shelter in the URL (/shelter/:shelterId/finance).
// ===========================================================================

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const FinancialDashboardPage: React.FC = () => {
  const { shelterId } = useParams<{ shelterId: string }>();
  const { data: transactions, loading: txLoading } = useFinancialTransactions(shelterId);
  const { data: campaignsData, loading: campLoading } = useCampaigns(shelterId);

  // Use real data, fall back to empty arrays
  const txData = transactions ?? [];
  const campData = campaignsData ?? [];

  const totalIncome = txData.filter(t => t.type === 'Income').reduce((s, t) => s + t.amount, 0);
  const totalExpense = txData.filter(t => t.type === 'Expense').reduce((s, t) => s + t.amount, 0);
  const balance = totalIncome - totalExpense;
  const mrr = txData.filter(t => t.type === 'Income' && (t.category === 'Subscription' || t.isRecurring)).reduce((s, t) => s + t.amount, 0);

  const expenseByCategory = txData.filter(t => t.type === 'Expense').reduce((acc, t) => { acc[t.category] = (acc[t.category] || 0) + t.amount; return acc; }, {} as Record<string, number>);
  const pieData = Object.entries(expenseByCategory).map(([name, value]) => ({ name, value }));

  const campaigns = campData.filter(c => c.active || c.isActive).slice(0, 5);

  if (txLoading || campLoading) {
    return (
      <div className="space-y-8">
        <div><h1 className="text-3xl font-bold text-gray-900">Pénzügyi Kimutatások</h1><p className="text-gray-600 mt-1">ReHome platform tranzakciók</p></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Skeleton className="h-28 w-full rounded-xl" />
          <Skeleton className="h-28 w-full rounded-xl" />
          <Skeleton className="h-28 w-full rounded-xl" />
          <Skeleton className="h-28 w-full rounded-xl" />
        </div>
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div><h1 className="text-3xl font-bold text-gray-900">Pénzügyi Kimutatások</h1><p className="text-gray-600 mt-1">ReHome platform tranzakciók</p></div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <KpiCard icon={<TrendingUp className="w-6 h-6" />} color="emerald" label="Összes Bevétel" value={formatHUF(totalIncome)} badge="Bevétel" />
        <KpiCard icon={<TrendingDown className="w-6 h-6" />} color="rose" label="Összes Kiadás" value={formatHUF(totalExpense)} badge="Kiadás" />
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between relative overflow-hidden">
          <div className={`absolute right-0 top-0 p-16 rounded-bl-full opacity-10 ${balance >= 0 ? 'bg-emerald-500' : 'bg-rose-500'}`} />
          <div className="flex items-center justify-between mb-4 relative z-10"><div className={`p-3 rounded-full ${balance >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}><Wallet className="w-6 h-6" /></div><span className="text-xs text-gray-400">Aktuális</span></div>
          <div className="relative z-10"><p className="text-sm text-gray-500 font-medium">Egyenleg</p><p className={`text-2xl font-bold ${balance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{balance > 0 ? '+' : ''}{formatHUF(balance)}</p></div>
        </div>
        <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 p-6 rounded-xl shadow-sm border border-indigo-500 flex flex-col justify-between text-white">
          <div className="flex items-center justify-between mb-4"><div className="p-3 rounded-full bg-white/20"><Repeat className="w-6 h-6 text-white" /></div><span className="text-xs font-bold bg-white/20 text-white px-2 py-1 rounded-full">MRR</span></div>
          <div><p className="text-sm text-indigo-100 font-medium">Ismétlődő Támogatás</p><p className="text-2xl font-bold">{formatHUF(mrr)}</p></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100"><h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Gift className="w-5 h-5 text-teal-600" /> Aktív Kampányok</h2></div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-gray-500 font-medium"><tr><th className="px-6 py-4">Kampány</th><th className="px-6 py-4">Célösszeg</th><th className="px-6 py-4">Gyűlt</th><th className="px-6 py-4 w-1/3">Progress</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {campaigns.map(c => {
                  const raised = getAmount(c, 'raisedAmount', 'raised_amount');
                  const target = c.targetAmount || 0;
                  const pct = target > 0 ? Math.round((raised / target) * 100) : 0;
                  return (
                  <tr key={c.id} className="hover:bg-gray-50"><td className="px-6 py-4 font-bold text-gray-800">{c.title}</td><td className="px-6 py-4 text-gray-600">{formatHUF(target)}</td><td className="px-6 py-4 text-green-600 font-bold">{formatHUF(raised)}</td><td className="px-6 py-4"><div className="flex items-center gap-3"><div className="flex-1 bg-gray-200 rounded-full h-2"><div className="bg-teal-500 h-2 rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} /></div><span className="text-xs font-bold">{pct}%</span></div></td></tr>
                  );
                })}
                {campaigns.length === 0 && (
                  <tr><td colSpan={4} className="px-6 py-8 text-center text-gray-400">Nincsenek aktív kampányok.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2"><PieIcon className="w-5 h-5 text-gray-500" />Kiadások Megoszlása</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart><Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">{pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={(v: any) => formatHUF(Number(v) || 0)} /></PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100"><h2 className="text-lg font-bold text-gray-800">Legutóbbi Tranzakciók</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-500"><tr><th className="px-6 py-4">Dátum</th><th className="px-6 py-4">Leírás</th><th className="px-6 py-4">Kategória</th><th className="px-6 py-4 text-right">Összeg</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {txData.map(t => (
                <tr key={t.id} className="hover:bg-gray-50"><td className="px-6 py-4 text-gray-500">{t.date || t.createdAt}</td><td className="px-6 py-4 font-medium">{t.description || '-'}</td><td className="px-6 py-4"><span className={`text-xs font-bold px-2 py-1 rounded ${t.type === 'Income' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>{t.category}</span></td><td className={`px-6 py-4 text-right font-bold ${t.type === 'Income' ? 'text-emerald-600' : 'text-rose-600'}`}>{t.type === 'Income' ? '+' : '-'}{formatHUF(t.amount)}</td></tr>
              ))}
              {txData.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-8 text-center text-gray-400">Még nincsenek tranzakciók.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Tailwind cannot see dynamically composed class names (`bg-${color}-50`),
// so the color variants must be spelled out statically.
const KPI_COLOR_STYLES: Record<string, { icon: string; badge: string }> = {
  emerald: { icon: 'bg-emerald-50 text-emerald-600', badge: 'bg-emerald-100 text-emerald-800' },
  rose: { icon: 'bg-rose-50 text-rose-600', badge: 'bg-rose-100 text-rose-800' },
  teal: { icon: 'bg-teal-50 text-teal-600', badge: 'bg-teal-100 text-teal-800' },
};

const KpiCard: React.FC<{ icon: React.ReactNode; color: string; label: string; value: string; badge: string }> = ({ icon, color, label, value, badge }) => {
  const styles = KPI_COLOR_STYLES[color] ?? KPI_COLOR_STYLES.teal;
  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-full ${styles.icon}`}>{icon}</div>
        <span className={`text-xs font-medium ${styles.badge} px-2 py-1 rounded-full flex items-center`}><ArrowUpRight className="w-3 h-3 mr-1" />{badge}</span>
      </div>
      <div><p className="text-sm text-gray-500 font-medium">{label}</p><p className="text-2xl font-bold text-gray-900">{value}</p></div>
    </div>
  );
};

export default FinancialDashboardPage;
